import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FiX, FiSearch, FiCheck, FiSend, FiRefreshCw, FiAlertTriangle } from 'react-icons/fi';
import { FaCreditCard } from 'react-icons/fa';
import styled, { keyframes } from 'styled-components';
import { addMonths } from 'date-fns';
import api from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/Toast';
import { useModal } from '../../../../../../hooks/Modal';
import { useAuth } from '../../../../../../hooks/Auth';
import { useSearch } from '../../../../../../hooks/Search';

// =========================================================
// Endpoints
// =========================================================
const LIST_ENDPOINT = '/sales-invoice';
const EMIT_ENDPOINT = '/emit-invoice-payment'; // POST /emit-invoice-payment/:invoice_hash
const CARD_METHOD = 'credit_card_recurring';

// Regra de encargos:
//   - encargo existe assim que o VENCIMENTO PASSA; zero enquanto não passou.
//   - multa: 2% da base, uma vez
//   - juros: 1% por MÊS de atraso, com qualquer mês iniciado contando cheio (teto):
//            1 dia => 1 mês (1%); 1 mês e 1 dia => 2 meses (2%); e assim por diante.
//            Meses de calendário reais (addMonths), não blocos de 30 dias.
// A base é `price ?? installment_value`: o ReprocessInvoices preserva o valor
// limpo em `price` e infla o installment_value; usar a base evita empilhar juros.
// IMPORTANTE: quem define o valor cobrado é o backend (emitPayment). Esta fórmula
// é só a PRÉVIA da tela e precisa ficar idêntica à do backend.
const RATE_MULTA = 0.02;
const RATE_JUROS_MES = 0.01;

// =========================================================
// Tipos
// =========================================================
interface Invoice {
  _id: string;
  installment_number: number;
  payment_date: string;
  installment_value: number;
  price?: number;
  juros?: number;
  multa?: number;
  payment_status_id: string;
  payment_method_id: string;
  invoice_hash: string;
  project_id?: string;
  client_id?: string;
  client_id_response?: { name?: string };
}

interface Charges {
  base: number;
  daysOverdue: number;
  juros: number;
  multa: number;
  final: number;
}

type RowStatus = 'idle' | 'sending' | 'done' | 'error';

// discriminated union para a confirmação
type ConfirmState =
  | { kind: 'one'; invoice: Invoice; withInterest: boolean }
  | { kind: 'all' }
  | null;

// =========================================================
// Helpers
// =========================================================
const formatBRL = (cents: number) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const formatDateBR = (d: string | Date | null) => {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '—';
  const [y, m, day] = dt.toISOString().slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

const invoiceMonth = (d: string) => {
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 7);
};
const currentMonth = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (key: string) => {
  if (!key) return '';
  const [y, m] = key.split('-').map(Number);
  const l = new Date(y, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return l.charAt(0).toUpperCase() + l.slice(1);
};

// cálculo de encargos (front) — espelha o backend; base = price ?? installment_value
// meses de atraso, com mês iniciado contando cheio: 1d => 1; 1 mês e 1d => 2; ...
const monthsLate = (due: Date): number => {
  if (Date.now() <= due.getTime()) return 0;
  let months = 1;
  let anchor = addMonths(due, 1);
  while (anchor.getTime() < Date.now()) { anchor = addMonths(anchor, 1); months += 1; }
  return months;
};

const computeCharges = (inv: Invoice): Charges => {
  const base = Number(inv.price ?? inv.installment_value ?? 0);
  const due = inv.payment_date ? new Date(inv.payment_date) : null;
  const daysOverdue = due ? Math.max(0, Math.floor((Date.now() - due.getTime()) / 86400000)) : 0;
  // encargo só quando o vencimento já passou
  if (!due || daysOverdue <= 0) return { base, daysOverdue: 0, juros: 0, multa: 0, final: base };
  const months = monthsLate(due);
  const multa = Math.round(base * RATE_MULTA);              // 2% (uma vez)
  const juros = Math.round(base * RATE_JUROS_MES * months); // 1% por mês de atraso (mês iniciado = cheio)
  return { base, daysOverdue, juros, multa, final: base + juros + multa };
};

// =========================================================
// Styled
// =========================================================
const spin = keyframes`from { transform: rotate(0); } to { transform: rotate(360deg); }`;

const Container = styled.div`
  position: relative;
  background: #fff; border-radius: 10px; min-width: 360px; max-width: 1040px; width: 100%;
  color: #1f2937; font-family: 'Inter', -apple-system, sans-serif;
  display: flex; flex-direction: column; max-height: 82vh;
`;
const Header = styled.div`padding: 18px 22px; border-bottom: 1px solid #f3f4f6; display: flex; align-items: center; gap: 12px;`;
const HeaderTitle = styled.div`
  flex: 1;
  h3 { font-size: 16px; font-weight: 800; color: #111827; }
  p { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;
const HeaderClose = styled.button`
  background: none; border: none; cursor: pointer; color: #6b7280; display: inline-flex; align-items: center;
  &:hover { color: #333; }
`;
const Body = styled.div`padding: 18px 22px; overflow-y: auto; flex: 1;`;
const Footer = styled.div`
  padding: 14px 22px; border-top: 1px solid #f3f4f6;
  display: flex; gap: 10px; justify-content: flex-end; align-items: center;
`;
const Filters = styled.div`
  display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px; margin-bottom: 16px;
  .field { display: flex; flex-direction: column; gap: 5px; }
  label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
  input {
    height: 40px; padding: 0 12px; border: 1.5px solid #e5e7eb; border-radius: 8px;
    font-size: 13px; font-family: inherit; background: #fff;
    &:focus { outline: none; border-color: #333; }
  }
`;
const BtnSearch = styled.button`
  height: 40px; padding: 0 16px; border: none; border-radius: 8px; cursor: pointer;
  background: #333; color: #fff; font-size: 13px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 6px;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const SummaryBar = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
  span.count { font-size: 12px; color: #6b7280; }
  span.count strong { color: #111827; }
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12px;
  th {
    text-align: left; padding: 9px 8px; font-size: 11px; font-weight: 600; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.03em; border-bottom: 1px solid #e5e7eb; background: #fafafa; white-space: nowrap;
  }
  td { padding: 9px 8px; border-bottom: 1px solid #f3f4f6; color: #1f2937; vertical-align: middle; }
  tr.done td { background: rgba(22,163,74,.06); }
  tr.overdue td.days { color: #dc2626; font-weight: 700; }
  .num { font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
  .muted { color: #9ca3af; font-size: 11px; }
  .final { font-weight: 700; }
  .actions { white-space: nowrap; text-align: right; }
`;
const BtnLink = styled.button<{ $variant: 'plain' | 'interest'; $status?: RowStatus }>`
  height: 28px; padding: 0 10px; border-radius: 6px; cursor: pointer; font-size: 11px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 5px; font-family: inherit; border: 1.5px solid;
  & + & { margin-left: 6px; }
  ${({ $variant }) =>
    $variant === 'interest'
      ? `background: #333; color: #fff; border-color: #333;`
      : `background: #fff; color: #6b7280; border-color: #e5e7eb;`}
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const DoneTag = styled.span`
  display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700;
  color: #16a34a; background: #f0fdf4; border: 1.5px solid #bbf7d0; border-radius: 6px; padding: 4px 10px;
`;
const Empty = styled.div`text-align: center; padding: 40px 0; color: #9ca3af; font-size: 13px;`;
const Spinner = styled.div<{ $c?: string }>`
  width: 13px; height: 13px; border: 2px solid #e5e7eb; border-top-color: ${({ $c }) => $c || 'currentColor'};
  border-radius: 999px; animation: ${spin} 1s linear infinite; display: inline-block;
`;
const BtnCancel = styled.button`
  height: 38px; padding: 0 16px; border: 1.5px solid #e5e7eb; border-radius: 8px; cursor: pointer;
  background: #fff; color: #6b7280; font-size: 13px; font-weight: 600;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const BtnPrimary = styled.button`
  height: 38px; padding: 0 18px; border: none; border-radius: 8px; cursor: pointer;
  background: #333; color: #fff; font-size: 13px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 6px;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
// --- overlay de confirmação ---
const Overlay = styled.div`
  position: absolute; inset: 0; background: rgba(17,24,39,.45);
  display: flex; align-items: center; justify-content: center; padding: 20px; border-radius: 10px; z-index: 5;
`;
const ConfirmCard = styled.div`
  background: #fff; border-radius: 12px; width: 100%; max-width: 420px; box-shadow: 0 10px 40px rgba(0,0,0,.25);
  overflow: hidden;
  .head { padding: 16px 20px; border-bottom: 1px solid #f3f4f6; }
  .head h4 { font-size: 15px; font-weight: 800; color: #111827; }
  .head p { font-size: 12px; color: #6b7280; margin-top: 2px; }
  .rows { padding: 14px 20px; }
  .r { display: flex; justify-content: space-between; padding: 5px 0; font-size: 13px; }
  .r span:first-child { color: #6b7280; }
  .r span:last-child { font-variant-numeric: tabular-nums; }
  .r.total { border-top: 1px solid #e5e7eb; margin-top: 6px; padding-top: 10px; font-weight: 800; }
  .r.total span:last-child { color: #333; font-size: 15px; }
  .warn {
    margin: 0 20px 14px; padding: 8px 10px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px;
    font-size: 11px; color: #92400e; display: flex; gap: 6px; align-items: flex-start;
  }
  .foot { padding: 14px 20px; border-top: 1px solid #f3f4f6; display: flex; gap: 10px; justify-content: flex-end; align-items: center; }
`;
const ProgressInfo = styled.div`
  font-size: 12px; color: #6b7280; flex: 1;
  strong { color: #333; font-weight: 700; }
`;

// =========================================================
// Flow
// =========================================================
const EmitCardChargesFlow: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addToast } = useToast();
  const { handleApiErrors } = useAuth();
  const { reloadSearchAll } = useSearch();

  const [month, setMonth] = useState<string>(currentMonth());
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [rowState, setRowState] = useState<Record<string, RowStatus>>({});

  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [busy, setBusy] = useState(false); // confirmando/emitindo (single ou all)
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // ----- buscar -----
  const search = useCallback(async () => {
    if (!month) { addToast({ title: 'Selecione o mês', type: 'error' }); return; }
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get(LIST_ENDPOINT, {
        params: { limitItems: 1000000, where: { payment_status_id: 'waiting', payment_method_id: CARD_METHOD } },
      });
      const list: Invoice[] = (data?.rows || []).filter((inv: Invoice) => {
        if (inv.payment_status_id !== 'waiting') return false;
        if (inv.payment_method_id !== CARD_METHOD) return false;
        if (!inv.payment_date) return false;
        return invoiceMonth(inv.payment_date) === month;
      });
      list.sort((a, b) => (a.client_id_response?.name || '').localeCompare(b.client_id_response?.name || ''));
      setInvoices(list);
      setRowState({});
    } catch (err) {
      addToast({ title: 'Erro ao carregar cobranças', type: 'error' });
      handleApiErrors(err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [month, addToast, handleApiErrors]);

  useEffect(() => { search(); }, []);

  const charges = useMemo(() => {
    const map: Record<string, Charges> = {};
    invoices.forEach(i => { map[i._id] = computeCharges(i); });
    return map;
  }, [invoices]);

  const doneCount = useMemo(() => Object.values(rowState).filter(s => s === 'done').length, [rowState]);
  const pending = useMemo(
    () => invoices.filter(i => rowState[i._id] !== 'done' && rowState[i._id] !== 'sending'),
    [invoices, rowState],
  );
  const totalComJuros = useMemo(
    () => pending.reduce((acc, i) => acc + charges[i._id].final, 0),
    [pending, charges],
  );

  // ----- emissão de uma fatura -----
  // Backend é a autoridade do valor: recebe só o modo e calcula/persiste juros+multa.
  // Flag como STRING ('yes' | 'no'), nunca boolean — o default do back é 'yes', e só o
  // literal 'no' desliga os juros (evita que um false colapse no default).
  const emitOne = async (inv: Invoice, withInterest: boolean): Promise<boolean> => {
    if (rowState[inv._id] === 'done' || rowState[inv._id] === 'sending') return false;
    setRowState(s => ({ ...s, [inv._id]: 'sending' }));
    try {
      await api.post(`${EMIT_ENDPOINT}/${inv.invoice_hash}`, { with_interest: withInterest ? 'yes' : 'no' });
      setRowState(s => ({ ...s, [inv._id]: 'done' }));
      return true;
    } catch (err: any) {
      setRowState(s => ({ ...s, [inv._id]: 'error' }));
      addToast({
        title: err?.response?.data?.message || err?.response?.data?.error || 'Erro ao emitir cobrança',
        type: 'error',
      });
      return false;
    }
  };

  // ----- confirmar (single) -----
  const runConfirmOne = async () => {
    if (confirm?.kind !== 'one') return;
    setBusy(true);
    const ok = await emitOne(confirm.invoice, confirm.withInterest);
    setBusy(false);
    setConfirm(null);
    if (ok) { addToast({ title: 'Cobrança emitida!', type: 'success' }); reloadSearchAll(); }
  };

  // ----- confirmar (todas, com juros) -----
  const runConfirmAll = async () => {
    const list = [...pending];
    if (list.length === 0) { setConfirm(null); return; }
    setBusy(true);
    setProgress({ done: 0, total: list.length });
    let ok = 0, fail = 0;
    for (let i = 0; i < list.length; i++) {
      const success = await emitOne(list[i], true); // com juros (auto-zera se em dia)
      success ? ok++ : fail++;
      setProgress({ done: i + 1, total: list.length });
    }
    setBusy(false);
    setConfirm(null);
    addToast({
      title: `${ok} cobrança(s) emitida(s)` + (fail ? ` · ${fail} com erro` : ''),
      type: fail ? 'error' : 'success',
    });
    reloadSearchAll();
  };

  // ----- render confirmação -----
  const renderConfirm = () => {
    if (!confirm) return null;

    if (confirm.kind === 'one') {
      const c = charges[confirm.invoice._id] || computeCharges(confirm.invoice);
      const withInterest = confirm.withInterest;
      const juros = withInterest ? c.juros : 0;
      const multa = withInterest ? c.multa : 0;
      const total = c.base + juros + multa;
      return (
        <Overlay>
          <ConfirmCard>
            <div className="head">
              <h4>{withInterest ? 'Emitir com juros' : 'Emitir sem juros'}</h4>
              <p>{confirm.invoice.client_id_response?.name} · parcela #{confirm.invoice.installment_number}</p>
            </div>
            <div className="rows">
              <div className="r"><span>Valor original</span><span>R$ {formatBRL(c.base)}</span></div>
              <div className="r"><span>Juros{c.daysOverdue ? ` (${c.daysOverdue}d atraso)` : ''}</span><span>R$ {formatBRL(juros)}</span></div>
              <div className="r"><span>Multa</span><span>R$ {formatBRL(multa)}</span></div>
              <div className="r total"><span>Total a cobrar</span><span>R$ {formatBRL(total)}</span></div>
            </div>
            {withInterest && c.daysOverdue === 0 && (
              <div className="warn"><FiAlertTriangle size={13} /> Fatura em dia: não há juros nem multa a aplicar.</div>
            )}
            <div className="foot">
              <BtnCancel onClick={() => setConfirm(null)} disabled={busy}>Cancelar</BtnCancel>
              <BtnPrimary onClick={runConfirmOne} disabled={busy}>
                {busy ? <><Spinner $c="#fff" /> Emitindo…</> : <><FiCheck size={14} /> Confirmar cobrança</>}
              </BtnPrimary>
            </div>
          </ConfirmCard>
        </Overlay>
      );
    }

    // all
    return (
      <Overlay>
        <ConfirmCard>
          <div className="head">
            <h4>Emitir todas · com juros</h4>
            <p>{pending.length} fatura(s) pendente(s)</p>
          </div>
          <div className="rows">
            <div className="r total"><span>Total estimado</span><span>R$ {formatBRL(totalComJuros)}</span></div>
          </div>
          <div className="warn">
            <FiAlertTriangle size={13} />
            Cobra com juros/multa quem está em atraso; faturas em dia são cobradas sem encargos. Ação em massa no cartão — sem desfazer.
          </div>
          <div className="foot">
            {busy
              ? <ProgressInfo><Spinner $c="#333" style={{ marginRight: 6, verticalAlign: 'middle' }} /> {progress.done}/{progress.total}</ProgressInfo>
              : <div style={{ flex: 1 }} />}
            <BtnCancel onClick={() => setConfirm(null)} disabled={busy}>Cancelar</BtnCancel>
            <BtnPrimary onClick={runConfirmAll} disabled={busy}>
              {busy ? <>Emitindo…</> : <><FiSend size={14} /> Emitir {pending.length}</>}
            </BtnPrimary>
          </div>
        </ConfirmCard>
      </Overlay>
    );
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <h3>Emitir cobranças no cartão</h3>
          <p>Faturas aguardando, no cartão, com vencimento no mês selecionado (todos os projetos).</p>
        </HeaderTitle>
        <HeaderClose onClick={onClose} disabled={busy}><FiX size={18} /></HeaderClose>
      </Header>

      <Body>
        <Filters>
          <div className="field">
            <label>Mês de vencimento</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') search(); }} disabled={loading || busy} />
          </div>
          <BtnSearch onClick={search} disabled={loading || busy}><FiSearch size={14} /> Buscar</BtnSearch>
        </Filters>

        {loading ? (
          <Empty><Spinner $c="#333" /> <div style={{ marginTop: 8 }}>Carregando…</div></Empty>
        ) : !searched ? (
          <Empty>Selecione o mês e clique em Buscar.</Empty>
        ) : invoices.length === 0 ? (
          <Empty>Nenhuma cobrança no cartão aguardando em {monthLabel(month)}.</Empty>
        ) : (
          <>
            <SummaryBar>
              <span className="count">
                <strong>{invoices.length}</strong> cobrança(s)
                {doneCount > 0 && <> · {doneCount} emitida(s)</>}
              </span>
            </SummaryBar>

            <Table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Projeto</th>
                  <th>Vencimento</th>
                  <th className="num">Original</th>
                  <th className="num">Juros</th>
                  <th className="num">Multa</th>
                  <th className="num">Final</th>
                  <th className="num">Atraso</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const c = charges[inv._id];
                  const st = rowState[inv._id] || 'idle';
                  return (
                    <tr key={inv._id} className={`${st === 'done' ? 'done' : ''} ${c.daysOverdue > 0 ? 'overdue' : ''}`}>
                      <td>{inv.client_id_response?.name || '—'}</td>
                      <td className="muted">{inv.project_id || '—'}</td>
                      <td>{formatDateBR(inv.payment_date)}</td>
                      <td className="num">R$ {formatBRL(c.base)}</td>
                      <td className="num">R$ {formatBRL(c.juros)}</td>
                      <td className="num">R$ {formatBRL(c.multa)}</td>
                      <td className="num final">R$ {formatBRL(c.final)}</td>
                      <td className="num days">{c.daysOverdue > 0 ? `${c.daysOverdue}d` : 'em dia'}</td>
                      <td className="actions">
                        {st === 'sending' ? (
                          <DoneTag style={{ color: '#6b7280', background: '#f9fafb', borderColor: '#e5e7eb' }}>
                            <Spinner /> Emitindo…
                          </DoneTag>
                        ) : st === 'done' ? (
                          <DoneTag><FiCheck size={12} /> Emitida</DoneTag>
                        ) : (
                          <>
                            <BtnLink $variant="plain" disabled={busy}
                              onClick={() => setConfirm({ kind: 'one', invoice: inv, withInterest: false })}>
                              Sem juros
                            </BtnLink>
                            <BtnLink $variant="interest" disabled={busy}
                              onClick={() => setConfirm({ kind: 'one', invoice: inv, withInterest: true })}>
                              {st === 'error' ? 'Repetir c/ juros' : 'Com juros'}
                            </BtnLink>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Body>

      {searched && invoices.length > 0 && (
        <Footer>
          <BtnCancel onClick={search} disabled={loading || busy}>
            <FiRefreshCw size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Recarregar
          </BtnCancel>
          <BtnPrimary onClick={() => setConfirm({ kind: 'all' })} disabled={busy || pending.length === 0}>
            <FiSend size={14} /> Emitir todas com juros {pending.length > 0 ? `(${pending.length})` : ''}
          </BtnPrimary>
        </Footer>
      )}

      {renderConfirm()}
    </Container>
  );
};

// =========================================================
// Botão exportado (headModules da SalesInvoice)
// =========================================================
export default function EmitirCobrancasCartao(): JSX.Element {
  const { addModal, removeModal } = useModal();
  const open = () => {
    addModal({
      key: 'emit-card-charges',
      title: '',
      theme: 'whiteModal',
      content: <EmitCardChargesFlow onClose={() => removeModal('emit-card-charges')} />,
    });
  };
  return (
    <button className="searchButton" title="Emitir cobranças no cartão do mês" type="button" onClick={open}>
      <FaCreditCard />
    </button>
  );
}
